import React, { useState, useEffect, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Tooltip,
  Button,
  Dialog,
  CircularProgress,
  Autocomplete,
  IconButton,
  TextField,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useAuthContext } from '../../hooks/useAuthContext'
import ApproveRejectSwitch from './ApproveRejectSwitch'
// import { useProcessContext } from '../../store'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SnoozeIcon from '@mui/icons-material/Snooze'

import {
  ACTIVITY_TYPES,
  TOAST_SETTINGS,
  TOAST_SETTINGS_ERROR,
  FIELD_TYPES,
  PROCESS_ACTIVITY_STATUSES,
  ROUTE_TYPES,
  FORMATS,
} from '../../utility/constants'
import { processDataService } from '../../services/processDataService'
import { formatCurrency } from '../../pages/DataEntryPage/utils/currency'
import LongMenu from '../../pages/DataEntryPage/Fields/LongMenu'
import { getPDFPreviewData } from '../../utility/utilityFunctions'
import { GetApp, Visibility } from '@mui/icons-material'
import FilePreview from './PreviewComponent/components/FilePreview'
import MultilineEllipsisWithTooltip from './MultilineEllipsisWithTooltip'
import ConfirmationBox from './ConfirmationBox'
import { getFieldForSetting } from '../../pages/DataEntryPage/utils/fieldUtil'
import TableSetting from '../../../src/components/Dashboard/PreviewComponent/TableSettings/TableSetting'

const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
const MyTaskTableView = ({ tasks, activityType, setActivityType, users }) => {
  const { user } = useAuthContext()
  const navigate = useNavigate()
  const [filteredTasks, setFilteredTasks] = useState([])
  const [overallComments, setOverallComments] = useState({})
  const [fieldComments, setFieldComments] = useState({})
  const [voteValues, setVoteValues] = useState({})
  const [selectedRoutes, setSelectedRoutes] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [groupedTasks, setGroupedTasks] = useState({})
  const [previewLoading, setPreviewLoading] = useState(false)
  const [images, setImages] = useState([])
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [loading, setLoading] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(1)
  const grayHeaderColor = '#f0f3fd'
  const tableCellStyle = { backgroundColor: grayHeaderColor, textAlign: 'center' }
  const [noTasksForCurrentType, setNoTasksForCurrentType] = useState(false)
  const [expanded, setExpanded] = React.useState(0)
  const [isConfirmationBoxOpen, setIsConfirmationBoxOpen] = useState(false)
  const [confirmationTitle, setConfirmationTitle] = useState('')
  const [pendingTemplateId, setPendingTemplateId] = useState(null)
  const [hiddenColumnsByTemplate, setHiddenColumnsByTemplate] = useState(() => {
    const saved = localStorage.getItem('hiddenColumnsByTemplate')
    return saved ? JSON.parse(saved) : {}
  })

  useEffect(() => {
    localStorage.setItem('activityType', activityType)
  }, [activityType])

  useEffect(() => {
    const storedActivityType = localStorage.getItem('activityType')
    if (storedActivityType) {
      setActivityType(storedActivityType)
    }
  }, [setActivityType])

  useEffect(() => {
    if (!tasks.length) return
    const filterTasks = async () => {
      setFilteredTasks([])
      setNoTasksForCurrentType(false)
      setIsLoading(true)
      try {
        const filtered = tasks.filter((task) => {
          return (
            task.activity_type === activityType ||
            (task.activity_type === ACTIVITY_TYPES.LONG_RUNNING &&
              activityType === ACTIVITY_TYPES.DATA_ENTRY)
          )
        })

        setFilteredTasks(filtered)
        if (filtered.length === 0) {
          setNoTasksForCurrentType(true)
        } else {
          setNoTasksForCurrentType(false)
        }
      } catch (error) {
        console.error('Error filtering tasks:', error)
      } finally {
        setIsLoading(false)
      }
    }
    filterTasks()
  }, [activityType, tasks])

  useEffect(() => {
    const sortedTasks = [...filteredTasks]
    const grouped = sortedTasks.reduce((acc, task) => {
      const {
        process_template_id,
        process_template_name,
        process_reference,
        processDashboardFields,
        processDashboardValues,
        process_route_templates,
        process_id,
        activity_template_id,
        process_activity_status,
        overall_comments,
        selected_route_template_id,
        process_reference_num,
        activity_template_name,
        process_version,
        non_default_currency,
        is_immediate_action_required,
        activity_type,
      } = task

      if (!acc[process_template_id]) {
        acc[process_template_id] = {
          name: process_template_name,
          fields: processDashboardFields,
          tasks: [],
        }
      }
      const visibleFields = processDashboardFields?.filter((field) => !field.is_hidden)
      const visibleValues = processDashboardValues?.filter(
        (_, idx) => !processDashboardFields[idx].is_hidden
      )

      const isDuplicate = acc[process_template_id].tasks.find((t) => t.processId === process_id)
      if (!isDuplicate) {
        acc[process_template_id].tasks.push({
          reference: process_reference,
          values: visibleValues,
          voteOptions: process_route_templates.map((route) => ({
            name: route.route_template_name,
            route_type: route.route_type,
            id: route.id,
          })),
          routeTemplates: process_route_templates,
          processId: process_id,
          activityTemplateId: activity_template_id,
          status: process_activity_status,
          overallComment: overall_comments,
          selectedRouteTemplateId: selected_route_template_id,
          processReference: process_reference_num,
          process_version: process_version,
          activity_template_name: activity_template_name,
          ndc: non_default_currency,
          is_immediate_action_required: is_immediate_action_required,
          activity_type: activity_type,
          process_data_created_at: task.process_data_created_at,
        })
      }
      acc[process_template_id].tasks.sort((a, b) => {
        const dateA = new Date(a.process_data_created_at || 0)
        const dateB = new Date(b.process_data_created_at || 0)
        return dateB - dateA
      })

      acc[process_template_id].fields = visibleFields
      return acc
    }, {})

    // Sort tasks inside each group
    Object.values(grouped).forEach((group) => {
      group.tasks.sort((a, b) => {
        const dateA = new Date(a.process_data_created_at || 0)
        const dateB = new Date(b.process_data_created_at || 0)
        return dateB - dateA // descending
      })
    })
    // Sort groups by most recent task date (DESCENDING) — sort tasks again here to ensure correctness
    const sortedGrouped = Object.values(grouped)
      .sort((groupA, groupB) => {
        const aDate = new Date(groupA.tasks?.[0]?.process_data_created_at || 0)
        const bDate = new Date(groupB.tasks?.[0]?.process_data_created_at || 0)
        return bDate - aDate
      })
      .reduce((acc, group) => {
        acc.push(group)
        return acc
      }, [])
    setGroupedTasks(sortedGrouped)
  }, [filteredTasks])

  // Load hidden columns from localStorage on initial render
  useEffect(() => {
    const stored = localStorage.getItem('hiddenColumnsByTemplate')
    if (stored) {
      try {
        setHiddenColumnsByTemplate(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to parse hidden columns from localStorage:', e)
      }
    }
  }, [])

  const getOverallComment = (route) => {
    if (route?.route_type === 'progress') return 'Ok'
    if (route?.route_type === 'neutral') return 'Please check'
    if (route?.route_type === 'regress') return 'Please revisit'
    return ''
  }

  const getFieldComment = (value) => {
    if (value === 'Approve') return 'Approved'
    if (value === 'N/A') return ''
    if (value === 'Reject') return 'Rejected'
    return ''
  }

  const handleSwitchChange = (processId, value, options) => {
    setVoteValues((prevVotes) => ({
      ...prevVotes,
      [processId]: value,
    }))

    const voteOptions = options.filter((route) => {
      if (value === 'Approve') {
        return true
      } else if (value === 'Reject') {
        return route.route_type === ROUTE_TYPES.REGRESS || route.route_type === ROUTE_TYPES.NEUTRAL
      }
      return false
    })

    setSelectedRoutes((prevRoutes) => ({
      ...prevRoutes,
      [processId]: voteOptions[0]?.id,
    }))

    setOverallComments((prevComments) => ({
      ...prevComments,
      [processId]: getOverallComment(voteOptions[0]),
    }))

    setFieldComments((prevComments) => ({
      ...prevComments,
      [processId]: getFieldComment(value),
    }))
  }

  const handleVoteChange = (processId, routeId) => {
    const task = filteredTasks.find((task) => task.process_id === processId)
    const selectedRoute = task.process_route_templates.find((template) => template.id === routeId)
    if (selectedRoute) {
      setSelectedRoutes((prevRoutes) => ({
        ...prevRoutes,
        [processId]: selectedRoute.id,
      }))
      setOverallComments((prevComments) => ({
        ...prevComments,
        [processId]: getOverallComment(selectedRoute),
      }))
    }
  }

  const handleGoClick = async (templateId) => {
    const { tasks } = groupedTasks[templateId]
    let selectedTasks = []
    setIsLoading(true)
    tasks.forEach((task) => {
      const voteValue = voteValues[task.processId] || 'N/A'
      if (voteValue === 'N/A') return
      selectedTasks.push({
        processId: task.processId,
        selectedRoutesId: selectedRoutes[task.processId],
        reference: task.reference,
        vote: voteValue,
        fieldLevelComment: fieldComments[task.processId],
        overallComment: overallComments[task.processId],
      })
    })

    const selectedTaskIds = selectedTasks.map((it) => it.processId)
    const filteredSelectedTasks = filteredTasks.filter(
      (task) => !selectedTaskIds.includes(task.process_id)
    )

    try {
      const requests = selectedTasks?.map((task) => {
        const { processId, selectedRoutesId, vote, fieldLevelComment, overallComment } = task

        return processDataService.createNextProcess(user, processId, selectedRoutesId, {
          action: vote,
          overall_comment: overallComment,
          field_level_comment: fieldLevelComment,
        })
      })
      const postResults = await Promise.all(requests)

      const successfulTasks = selectedTasks.filter((_, index) =>
        [200, 201].includes(postResults[index].status)
      )
      const unsuccessfulTasks = selectedTasks.filter(
        (_, index) => ![200, 201].includes(postResults[index].status)
      )

      if (successfulTasks && postResults.length) {
        const references = selectedTasks.map((task) => task.reference).join(', ')
        toast.success(` ${references} Saved successfully`, TOAST_SETTINGS)

        // Proceed to make PUT requests after successful POSTs
        const putRequests = selectedTasks.map((task) => {
          const { processId } = task
          const comment = overallComments[processId]
          return processDataService.saveOverallComments(user, processId, comment.trim())
        })

        const putResults = await Promise.all(putRequests)
        if (putResults.every((result) => [200, 201].includes(result.status))) {
          setFilteredTasks(filteredSelectedTasks)
        }
      }

      if (unsuccessfulTasks.length) {
        const unsavedReferences = unsuccessfulTasks.map((task) => task.reference).join(', ')
        toast.error(` ${unsavedReferences} failed to save.`, TOAST_SETTINGS_ERROR)
      }
    } catch (error) {
      toast.error('Error saving data.', TOAST_SETTINGS_ERROR)
    } finally {
      setIsLoading(false)
    }
  }
  const handlePrepareConfirmation = (templateId) => {
    const { tasks } = groupedTasks[templateId] || {}
    const selectedTasks = tasks
      .filter((task) => (voteValues[task.processId] || 'N/A') !== 'N/A')
      .map((task) => ({
        reference: task.reference,
        processId: task.processId,
      }))

    if (selectedTasks.length === 0) {
      toast.error('Please select Approve/Reject All.', TOAST_SETTINGS_ERROR)
      return
    }

    const references = selectedTasks.map((task) => task.reference).join(', ')
    setConfirmationTitle(`Are you sure you want to submit: ${references}?`)
    setPendingTemplateId(templateId)
    setIsConfirmationBoxOpen(true)
  }

  const getPreviewData = async (fileKey) => {
    setPreviewLoading(true)
    const result = await getPDFPreviewData(fileKey, user)
    setImages(result)
    setPreviewLoading(false)
  }

  const handlePreviewClick = (fileKey) => {
    getPreviewData(fileKey)
    setPreviewLoading(true)
    setIsDialogOpen(true)
  }

  const handlePageChange = (_, value) => {
    setCurrentPage(value)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setPreviewLoading(false)
  }

  let axiosOptionsWithBlob = {}
  if (user) {
    axiosOptionsWithBlob = {
      headers: { Authorization: `Bearer ${user.token}` },
      responseType: 'blob',
    }
  }

  const getFileWithS3Key = async (s3Key, index) => {
    setLoading((prev) => new Set([...prev, index]))

    const result = await axios.get(
      `${NODE_BASE_URL}/data-entry/file/${s3Key}`,
      axiosOptionsWithBlob
    )

    const url = window.URL.createObjectURL(new Blob([result.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', s3Key)
    document.body.appendChild(link)
    link.click()

    setLoading((prev) => {
      const updated = new Set(prev)
      updated.delete(index)
      return updated
    })
  }

  const options = (x, y) => [
    {
      label: 'Preview',
      icon: (
        <IconButton size="small">
          {images == null ? <CircularProgress color="primary" size="25px" /> : <Visibility />}
        </IconButton>
      ),
      onClick: () => handlePreviewClick(x, y),
    },
    {
      label: 'Download',
      icon: (
        <IconButton size="small">
          {loading.has(y) ? (
            <CircularProgress color="info" size="25px" />
          ) : (
            <GetApp color="info" size="small" />
          )}
        </IconButton>
      ),
      onClick: () => getFileWithS3Key(x, y),
    },
  ]

  // Handler for hiding columns and persist to localStorage
  const handleSetHiddenColumns = (templateId, newHidden) => {
    const allowedHidden = newHidden.filter((id) => {
      const allFields = updatedGroupedTasks?.[templateId]?.finalFields || []
      const field = allFields.find((f) => f.id === id)
      return !field?.nonHideable
    })

    const newHiddenColumnsByTemplate = {
      ...hiddenColumnsByTemplate,
      [activityType]: {
        ...(hiddenColumnsByTemplate?.[activityType] || {}),
        [templateId]: allowedHidden,
      },
    }
    setHiddenColumnsByTemplate(newHiddenColumnsByTemplate)
    localStorage.setItem('hiddenColumnsByTemplate', JSON.stringify(newHiddenColumnsByTemplate))
  }

  // Function to get extra columns based on activity type and tasks
  const getExtraColumns = (tasks, activityType) => {
    const extra = [{ key: 'reference', label: 'Reference Number' }]
    if (activityType === ACTIVITY_TYPES.REVIEW) {
      if (tasks.some((task) => task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED)) {
        extra.push({ key: 'review', label: 'Review' })
      }
      extra.push({ key: 'vote', label: 'Vote' }, { key: 'activity', label: 'Activity (v)' })
      if (tasks.some((task) => task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED)) {
        extra.push({ key: 'fieldLevelComment', label: 'Field-level Common Comment' })
      }
      extra.push({ key: 'overallComment', label: 'Overall Comment' })
    }
    return extra
  }

  // Function to transform grouped tasks with extra columns and unique IDs
  const transformGroupedTasksWithExtra = (groupedTasks, activityType, hiddenColumns = []) => {
    const allTasks = Object.values(groupedTasks).flatMap((group) => group.tasks)
    const extraColumns = getExtraColumns(allTasks, activityType)
    const transformed = Object.entries(groupedTasks).reduce((acc, [templateId, group]) => {
      const originalFields = [...group.fields]
      const originalFieldsWithIds = originalFields.map((field, index) => {
        const id = field.id ?? `original-${index}`
        return {
          ...field,
          id,
          is_hidden: hiddenColumns.includes(id),
          nonHideable: field.key === 'process_reference_num', // prevent hiding
        }
      })

      acc[templateId] = {
        ...group,
        extraColumns,
        fields: originalFieldsWithIds,
        finalFields: [...originalFieldsWithIds],
      }
      return acc
    }, {})
    return transformed
  }

  // Transform grouped tasks with extra columns and hidden columns
  const updatedGroupedTasks = useMemo(
    () => transformGroupedTasksWithExtra(groupedTasks, activityType),
    [groupedTasks, activityType]
  )

  return (
    <div className="tableview">
      {noTasksForCurrentType ? (
        <>
          <Typography variant="body1" color="textSecondary" align="center">
            No tasks available for {activityType}
          </Typography>
        </>
      ) : (
        Object.entries(updatedGroupedTasks).map(([templateId, group], index) => {
          const hiddenColumns = hiddenColumnsByTemplate?.[activityType]?.[templateId] || []
          const { name, tasks, finalFields } = group
          const visibleFields = finalFields?.filter((f) => !hiddenColumns.includes(f.id))
          const filteredVisibleFields = visibleFields
          return (
            <>
              <div
                className="tableview-inside"
                key={templateId}
                style={{
                  padding: '0px 0px 10px',
                  borderRadius: '14px',
                }}
              >
                <Accordion
                  expanded={index === expanded}
                  className="accordian-shadow"
                  onChange={(_, isExpanded) => setExpanded(isExpanded ? index : false)}
                >
                  <AccordionSummary
                    className="acoordionsummary"
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <div
                      style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Typography
                          className="textPrimary fontsize14 mb-0"
                          variant="h6"
                          gutterBottom
                        >
                          {name}
                        </Typography>

                        {/* TableSetting appears in each summary, but only when expanded */}
                        {expanded === index && (
                          <div
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                            }}
                          >
                            <TableSetting
                              columns={getFieldForSetting(
                                finalFields?.filter((field) => !field.nonHideable)
                              )}
                              hiddenColumns={hiddenColumns}
                              setHiddenColumns={(newHidden) =>
                                handleSetHiddenColumns(templateId, newHidden)
                              }
                              showFreezing={false}
                              color="primary"
                            />
                          </div>
                        )}
                      </div>
                      <p className="textPrimary fontsize11 mb-0">
                        <span
                          style={{
                            padding: '3px 6px',
                            borderRadius: '10px',
                            width: '15px',
                            height: '15px',
                            backgroundColor: '#aeadfa',
                          }}
                        >
                          {tasks?.length || 0}
                        </span>
                      </p>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer
                      key={name}
                      component={Paper}
                      style={{ marginBottom: '3px' }}
                      sx={{ boxShadow: '1px 2px 6px 0px rgb(57 85 120 / 11%)' }}
                    >
                      <Table className="responsiveTable">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              className="fixed-width-cell sticky-left referenceNum-column"
                              sx={tableCellStyle}
                            >
                              Reference Number
                            </TableCell>

                            {activityType === ACTIVITY_TYPES.REVIEW && (
                              <>
                                {tasks.some(
                                  (task) => task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED
                                ) && (
                                  <TableCell
                                    className="fixed-width-cell sticky-left-2 review-column"
                                    sx={tableCellStyle}
                                  >
                                    Review
                                  </TableCell>
                                )}

                                <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                  Vote
                                </TableCell>
                                <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                  Activity (v)
                                </TableCell>
                                {tasks.some(
                                  (task) => task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED
                                ) && (
                                  <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                    Field-level Common Comment
                                  </TableCell>
                                )}
                                <TableCell className="fixed-width-cell" sx={tableCellStyle}>
                                  Overall Comment
                                </TableCell>
                              </>
                            )}
                            {filteredVisibleFields?.map((field) => (
                              <TableCell
                                key={field.id}
                                className="fixed-width-cell"
                                sx={{
                                  minWidth: `${field.field_width_in_rem_for_process_dashboard}rem`,
                                  backgroundColor: grayHeaderColor,
                                  // Dynamically set text alignment based on the field type
                                  textAlign: [
                                    FIELD_TYPES.DATE,
                                    FIELD_TYPES.DATETIME,
                                    FIELD_TYPES.CURRENCY,
                                    FIELD_TYPES.NUMBER,
                                  ].includes(field.data_structure)
                                    ? 'center'
                                    : 'center',
                                }}
                              >
                                {field.field_template_name}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>

                        {/* Table Data Row */}
                        <TableBody>
                          {tasks?.map((task, j) => {
                            const voteValue = voteValues[task.processId] || 'N/A'
                            const isDisabled = voteValue === 'N/A'
                            const formattedText = (task.overallComment || '')
                              .split('\n')
                              .map((line, index) => (
                                <React.Fragment key={index}>
                                  {line}
                                  <br />
                                </React.Fragment>
                              ))
                            const voteOptions = task.voteOptions.filter((route) => {
                              if (
                                voteValue === 'Approve' ||
                                task.status === PROCESS_ACTIVITY_STATUSES.CLOSED
                              ) {
                                return true
                              } else if (voteValue === 'Reject') {
                                return (
                                  route.route_type === ROUTE_TYPES.REGRESS ||
                                  route.route_type === ROUTE_TYPES.NEUTRAL
                                )
                              }
                              return false
                            })

                            return (
                              <TableRow
                                key={`${templateId}-${task.processId}-${task.activityTemplateId}-${j}`}
                                id={`${templateId}-${task.processId}-${task.activityTemplateId}`}
                                style={j % 2 !== 0 ? { backgroundColor: '#fff' } : null}
                              >
                                <Tooltip
                                  sx={{ padding: '8px', position: 'relative', top: '5px' }}
                                  title={
                                    task.status === PROCESS_ACTIVITY_STATUSES.ACTIVE
                                      ? !task.is_immediate_action_required &&
                                        task.activity_type === ACTIVITY_TYPES.LONG_RUNNING
                                        ? 'No immediate action required'
                                        : 'Work on task'
                                      : null
                                  }
                                >
                                  <TableCell
                                    sx={{ padding: '8px' }}
                                    align="center"
                                    className="sticky-left"
                                    style={{
                                      color:
                                        task.status == PROCESS_ACTIVITY_STATUSES.ACTIVE
                                          ? '#6C47E4'
                                          : null,
                                      fontWeight: 700,
                                    }}
                                  >
                                    <span
                                      onClick={() => {
                                        if (task.status === PROCESS_ACTIVITY_STATUSES.ACTIVE) {
                                          navigate(
                                            `/process-data/${task.processId}/${task.activityTemplateId}`
                                          )
                                        }
                                      }}
                                      style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <span>
                                        {task.activity_type === ACTIVITY_TYPES.LONG_RUNNING ? (
                                          !task.is_immediate_action_required ? (
                                            <SnoozeIcon fontSize="small" />
                                          ) : null
                                        ) : null}
                                      </span>{' '}
                                      {task.reference}
                                    </span>
                                  </TableCell>
                                </Tooltip>
                                {/* Approve/Reject All */}
                                {activityType === ACTIVITY_TYPES.REVIEW && (
                                  <>
                                    {task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED && (
                                      <TableCell
                                        sx={{ padding: '0px' }}
                                        align="center"
                                        className="sticky-left-2"
                                      >
                                        <ApproveRejectSwitch
                                          index={`${templateId}-${task.processId}`}
                                          switchState={voteValue}
                                          handleSwitchChange={(processId, value) =>
                                            handleSwitchChange(processId, value, task.voteOptions)
                                          }
                                          taskReference={task.processId}
                                        />
                                      </TableCell>
                                    )}
                                    {/* vote */}
                                    <TableCell
                                      sx={{ padding: '0px', minWidth: '180px' }}
                                      align="center"
                                    >
                                      {task.status === PROCESS_ACTIVITY_STATUSES.CLOSED ? (
                                        voteOptions.find(
                                          (it) => it.id === task.selectedRouteTemplateId
                                        )?.name
                                      ) : (
                                        <Autocomplete
                                          size="small"
                                          key={voteValue}
                                          options={voteOptions}
                                          sx={{ width: '100%' }}
                                          disabled={isDisabled}
                                          value={
                                            voteOptions.find(
                                              (it) => it.id === selectedRoutes[task.processId]
                                            ) || voteOptions[0]
                                          }
                                          onChange={(_, newValue) =>
                                            handleVoteChange(task.processId, newValue?.id)
                                          }
                                          getOptionLabel={(option) => option?.name}
                                          renderInput={(params) => (
                                            <TextField
                                              {...params}
                                              variant="outlined"
                                              size="small" // or "standard", "filled"
                                              InputProps={{
                                                ...params.InputProps,
                                                disableUnderline: true, // for "standard" and "filled" variants
                                                style: {
                                                  border: 'none',
                                                  outline: 'none',
                                                },
                                              }}
                                              sx={{
                                                width: '100%',
                                                '& .MuiOutlinedInput-root': {
                                                  '& fieldset': {
                                                    border: 'none',
                                                  },
                                                  '&:hover fieldset': {
                                                    border: 'none',
                                                  },
                                                  '&.Mui-focused fieldset': {
                                                    border: 'none',
                                                  },
                                                },
                                              }}
                                            />
                                          )}
                                        />
                                      )}
                                    </TableCell>

                                    {/*Activity Name */}
                                    <TableCell>
                                      <Typography
                                        sx={{
                                          fontSize: '12px',
                                          color: '#4b4f60',
                                          fontWeight: '600',
                                          width: '194px',
                                        }}
                                      >
                                        {task.activity_template_name} (v{task.process_version})
                                      </Typography>
                                    </TableCell>

                                    {/* Field-level Common Comment */}
                                    {task.status !== PROCESS_ACTIVITY_STATUSES.CLOSED && (
                                      <TableCell
                                        sx={{ padding: '0px', minWidth: '130px' }}
                                        align="left"
                                      >
                                        <TextField
                                          variant="outlined"
                                          size="small"
                                          disabled={isDisabled}
                                          multiline
                                          value={
                                            task.status === PROCESS_ACTIVITY_STATUSES.CLOSED
                                              ? task.overallComment || ''
                                              : fieldComments[task.processId] || ''
                                          }
                                          onChange={(e) =>
                                            setFieldComments((state) => ({
                                              ...state,
                                              [task.processId]: e.target.value,
                                            }))
                                          } // or "standard", "filled"
                                          InputProps={{
                                            disableUnderline: true, // for "standard" and "filled" variants
                                            style: {
                                              border: 'none',
                                              outline: 'none',
                                            },
                                          }}
                                          sx={{
                                            width: '100%',
                                            '& .MuiOutlinedInput-root': {
                                              '& fieldset': {
                                                border: 'none',
                                              },
                                              '&:hover fieldset': {
                                                border: 'none',
                                              },
                                              '&.Mui-focused fieldset': {
                                                border: 'none',
                                              },
                                            },
                                          }}
                                        />
                                      </TableCell>
                                    )}
                                    {/* Overall Comment */}

                                    <TableCell
                                      sx={{ padding: '0px', minWidth: '130px' }}
                                      align="left"
                                    >
                                      {task.status === PROCESS_ACTIVITY_STATUSES.CLOSED ? (
                                        <MultilineEllipsisWithTooltip
                                          text={task.overallComment || ''}
                                          maxLines={3}
                                        />
                                      ) : (
                                        <TextField
                                          size="small"
                                          multiline
                                          disabled={isDisabled}
                                          value={overallComments[task.processId] || ''}
                                          onChange={(e) =>
                                            setOverallComments((state) => ({
                                              ...state,
                                              [task.processId]: e.target.value,
                                            }))
                                          }
                                          variant="outlined" // or "standard", "filled"
                                          InputProps={{
                                            disableUnderline: true, // for "standard" and "filled" variants
                                            style: {
                                              border: 'none',
                                              outline: 'none',
                                            },
                                          }}
                                          sx={{
                                            width: '100%',
                                            '& .MuiOutlinedInput-root': {
                                              '& fieldset': {
                                                border: 'none',
                                              },
                                              '&:hover fieldset': {
                                                border: 'none',
                                              },
                                              '&.Mui-focused fieldset': {
                                                border: 'none',
                                              },
                                            },
                                          }}
                                        />
                                      )}
                                    </TableCell>
                                  </>
                                )}
                                {/* Custom alignment for currency, date, and number fields */}

                                {filteredVisibleFields?.map((field, idx) => {
                                  const value = task.values[idx]
                                  let displayValue

                                  // Custom alignment for 'DATE', 'CURRENCY', 'NUMBER' fields
                                  const cellAlign = [
                                    FIELD_TYPES.DATE,
                                    FIELD_TYPES.DATETIME,
                                    FIELD_TYPES.CURRENCY,
                                    FIELD_TYPES.NUMBER,
                                  ].includes(field.data_structure)
                                    ? 'right'
                                    : 'left' // Align right for date, currency, number, left for others

                                  if (field.data_structure === FIELD_TYPES.DATE) {
                                    displayValue =
                                      value && moment(value).isValid()
                                        ? moment(value).format(FORMATS.DATE)
                                        : ''
                                  } else if (
                                    field.data_structure === FIELD_TYPES.TIMESTAMPED_BIGTEXT &&
                                    Array.isArray(value)
                                  ) {
                                    const formattedJSX = value.map((item, index) => {
                                      const user = users?.find((u) => u.user_id === item.user_id)
                                      const username = user
                                        ? `${user.first_name} ${user.last_name}`
                                        : ''
                                      const timestamp = item.timestamp
                                      const text = item.text

                                      return (
                                        <React.Fragment key={index}>
                                          <strong style={{ color: '#4D5E80' }}>{timestamp}:</strong>{' '}
                                          {text}
                                          {username ? (
                                            <span
                                              style={{
                                                fontSize: '11px',
                                                fontStyle: 'italic',
                                                color: '#888',
                                                marginLeft: 6,
                                              }}
                                            >
                                              {' - '}
                                              {username}
                                            </span>
                                          ) : (
                                            ''
                                          )}
                                          <br />
                                        </React.Fragment>
                                      )
                                    })

                                    displayValue = (
                                      <MultilineEllipsisWithTooltip
                                        text={formattedJSX}
                                        maxLines={3}
                                      />
                                    )
                                  } else if (field.data_structure === FIELD_TYPES.CURRENCY) {
                                    const ndcMap = task.ndc.reduce((acc, cur) => {
                                      return { ...acc, [cur.field_template_id]: cur.currency_code }
                                    }, {})

                                    displayValue = formatCurrency(value, ndcMap[field.id], {
                                      defaultCurrency: user?.default_currency_code,
                                    })
                                  } else if (field.data_structure === FIELD_TYPES.FILE) {
                                    displayValue = (
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                        }}
                                      >
                                        {value}
                                        {value && <LongMenu options={options(value, idx)} />}
                                      </div>
                                    )
                                  } else {
                                    if (Array.isArray(value)) {
                                      // If it's an array of strings or numbers
                                      const arrayDisplay = value
                                        .map((item) =>
                                          typeof item === 'object' && item !== null
                                            ? item.value || JSON.stringify(item)
                                            : item
                                        )
                                        .filter(Boolean)
                                        .join(', ')
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={arrayDisplay}
                                          maxLines={3}
                                        />
                                      )
                                    } else if (typeof value === 'string' && value.includes('\n')) {
                                      const namesOnly = value.replace(/\s*\([^)]*\)/g, '')
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={namesOnly}
                                          maxLines={3}
                                        />
                                      )
                                    } else if (typeof value === 'object' && value !== null) {
                                      displayValue = (
                                        <MultilineEllipsisWithTooltip
                                          text={JSON.stringify(value)}
                                          maxLines={3}
                                        />
                                      )
                                    } else {
                                      displayValue = value
                                    }
                                  }

                                  return (
                                    <TableCell key={idx} align={cellAlign} sx={{ padding: '8px' }}>
                                      <MultilineEllipsisWithTooltip
                                        text={displayValue}
                                        maxLines={3}
                                      />
                                    </TableCell>
                                  )
                                })}
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>

                {activityType === ACTIVITY_TYPES.REVIEW &&
                  tasks &&
                  tasks.length > 0 &&
                  index === expanded &&
                  tasks.some((task) => task.status === PROCESS_ACTIVITY_STATUSES.ACTIVE) && (
                    <Button
                      className="defalut-btn mt-1"
                      color="primary"
                      variant="contained"
                      type="button"
                      onClick={() => handlePrepareConfirmation(templateId)}
                    >
                      {isLoading ? <CircularProgress size={24} /> : 'Go'}
                    </Button>
                  )}
              </div>
            </>
          )
        })
      )}

      <ConfirmationBox
        isDialogOpen={isConfirmationBoxOpen}
        title={confirmationTitle}
        setIsDialogOpen={setIsConfirmationBoxOpen}
        onYes={() => {
          setIsConfirmationBoxOpen(false)
          if (pendingTemplateId) {
            handleGoClick(pendingTemplateId)
            setPendingTemplateId(null)
          }
        }}
      />

      <Dialog open={isLoading} width="lg">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Typography variant="h6" color="textSecondary" style={{ marginBottom: '10px' }}>
            Saving your response...
          </Typography>
          <CircularProgress color="primary" size={30} />
        </div>
      </Dialog>

      <FilePreview
        images={images}
        currentPage={currentPage}
        previewLoading={previewLoading}
        isDialogOpen={isDialogOpen}
        onPageChange={handlePageChange}
        onPreviewClick={handlePreviewClick}
        onCloseDialog={handleCloseDialog}
      />
    </div>
  )
}

export default MyTaskTableView
